'use client';
import { useId as useNewsletterId } from 'react';
import { CaptchaRequestMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';

import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import { VscSend } from 'react-icons/vsc';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';

export function Newsletter() {
  const seoNewsletterId = useNewsletterId();
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: newsletterSubscribe,
    onSuccess: () => setIsSubscribed(true),
  });

  const locale = useLocaleStore(s => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <form
      className={cn(
        'flex w-full flex-col items-start justify-start gap-4',
        'ea-homepage-newsletter__form',
      )}
      onSubmit={handleSubmit}
    >
<CaptchaRequestMessage error={subscribeMutation.error} />
      <div className={cn('flex h-[50px] w-full flex-row items-center justify-between gap-2')}>
        <Input
          aria-label={systemLabels('email')}
          id={`newsletter-email-${seoNewsletterId}`}
          type="email"
          name="email"
          placeholder={t('text35')}
          onChange={e => setEmail(e.target.value)}
          className={{
            wrapper: cn(
              'h-[50px] w-full rounded-full bg-white/10',
              'ea-homepage-newsletter__form-item-email-input',
            ),
            main: cn('px-6 text-white placeholder:text-white/50'),
          }}
        />
        <Button
          type="submit"
          className={cn(
            'max-h-[50px] min-h-[50px] min-w-[50px] max-w-[50px] rounded-full p-0 sm:min-w-min sm:max-w-max sm:px-6',
            'ea-homepage-newsletter__form-item-email-submit-button',
          )}
          disabled={email.length === 0 || !checked || isSubscribed}
          isLoading={subscribeMutation.isPending}
          aria-label={t('text182')}
        >
          <VscSend size={24} className="block sm:hidden" aria-hidden="true" />
          <p className="hidden sm:block">{t('text182')}</p>
        </Button>
      </div>
      {isSubscribed && <span className={cn('font-semibold text-success')}>{t('text234')}</span>}
      <div
        className={cn(
          'flex flex-row items-start space-x-2',
          'ea-homepage-newsletter__form-item-gdpr',
        )}
      >
        <Checkbox
          checked={checked}
          onCheckedChange={() => setChecked(!checked)}
          id={`newsletter-gdpr-${seoNewsletterId}`}
          aria-label={t('text264') + ' ' + t('text265') + ' ' + t('text436')}
          className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox')}
          required
        />
        <Label
          htmlFor={`newsletter-gdpr-${seoNewsletterId}`}
          className={cn(
            'w-full max-w-[360px] text-sm font-light text-white',
            'ea-homepage-newsletter__form-item-gdpr-checkbox-label',
          )}
        >
          {t('text2341')}{' '}
          <Link
            target="_blank"
            prefetchOnHover
            href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.GDPR?.() ?? '/', locale, 'gdpr')}
            className="font-semibold text-primary"
          >
            {t('text2342')}
          </Link>
        </Label>
      </div>
    </form>
  );
}
